import React from 'react';
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Box,
  Chip,
  Icon,
  Tooltip
} from '@mui/material';
import {
  Description as DocumentIcon,
  Check as CheckIcon,
  Folder as PackageIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const TemplateCard = ({ 
  template, 
  isSelected, 
  onToggle 
}) => {
  const getCategoryColor = (category) => {
    const colors = {
      'wills': '#1976d2',
      'trusts': '#388e3c',
      'powers': '#f57c00',
      'healthcare': '#d32f2f',
      'authorizations': '#7b1fa2',
      'business': '#455a64',
      'packages': '#f57c00' // Orange for packages
    };
    return colors[category?.toLowerCase()] || '#757575';
  };
  
  const isPackage = template.type === 'package';

  return (
    <Card
      sx={{
        height: 350,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'divider',
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardActionArea
        onClick={() => onToggle(template)}
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start'
        }}
      >
        <CardContent sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          p: 2
        }}>
          {/* Selection indicator */}
          {isSelected && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
            </Box>
          )}

          {/* Category chip */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip
              label={isPackage ? 'PACKAGE' : template.category}
              size="small"
              sx={{
                bgcolor: getCategoryColor(template.category),
                color: 'white',
                fontWeight: 'bold',
                minWidth: 80,
                justifyContent: 'center'
              }}
            />
            {isPackage && (
              <Chip
                label={`${template.documentCount} documents`}
                size="small"
                variant="outlined"
                sx={{ borderColor: 'divider' }}
              />
            )}
          </Box>

          {/* Document/Package icon and name */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            {isPackage ? (
              <PackageIcon sx={{ mr: 1, mt: 0.5, color: 'warning.main' }} />
            ) : (
              <DocumentIcon sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
            )}
            <Tooltip title={template.displayName || template.name}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {template.displayName || template.name}
              </Typography>
            </Tooltip>
          </Box>

          {/* Description */}
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {template.description}
          </Typography>

          {/* Tips for attorneys */}
          {template.tips && (
            <Box sx={{ mt: 2, p: 1, bgcolor: 'info.light', borderRadius: 1, opacity: 0.7 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <InfoIcon sx={{ fontSize: 16, mr: 0.5, mt: 0.5, color: 'info.main', flexShrink: 0 }} />
                <Typography 
                  variant="caption" 
                  color="info.main"
                  sx={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {template.tips}
                </Typography>
              </Box>
            </Box>
          )}

          {/* File info or package contents */}
          {isPackage ? (
            <Box sx={{ mt: 2 }}>
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                Includes: {template.documents?.join(', ')}
              </Typography>
            </Box>
          ) : (
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ mt: 2, display: 'block' }}
            >
              {template.fileName}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default TemplateCard;